import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const newsletterUrl = 'https://readdy.ai/api/form/d8ruiq8n4gr6p3b2giqg';
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newsletterUrl) return;
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value as string);
    });
    setStatus('loading');
    try {
      await fetch(newsletterUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      {/* Upper Footer */}
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          {/* Column 1: About */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-store-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg font-bold text-background-50">
                Superbuy<br />Spreadsheet
              </span>
            </Link>
            <p className="font-body text-sm text-background-50/55 leading-relaxed">
              Your ultimate curated shopping resource. Thousands of handpicked products across
              14 categories with real QC photos, price comparisons, and safety tips.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="font-label text-xs uppercase tracking-[0.2em] text-background-50/40 mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="font-body text-sm text-background-50/70 hover:text-background-50 transition-colors duration-200">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/article/ultimate-superbuy-spreadsheet-guide-2026" className="font-body text-sm text-background-50/70 hover:text-background-50 transition-colors duration-200">
                  Ultimate Guide
                </Link>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="font-body text-sm text-background-50/70 hover:text-background-50 transition-colors duration-200 cursor-pointer"
                >
                  Main Store
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Categories */}
          <div>
            <h4 className="font-label text-xs uppercase tracking-[0.2em] text-background-50/40 mb-5">
              Top Categories
            </h4>
            <ul className="space-y-3">
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="font-body text-sm text-background-50/70 hover:text-background-50 transition-colors duration-200 cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div>
            <h4 className="font-label text-xs uppercase tracking-[0.2em] text-background-50/40 mb-5">
              Stay Updated
            </h4>
            <p className="font-body text-sm text-background-50/55 mb-4">
              Get the latest spreadsheet updates and new product discoveries.
            </p>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form>
              <div className="flex items-center border-b border-background-50/20 pb-2">
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                  disabled={status === 'loading'}
                  className="flex-1 bg-transparent text-background-50 font-body text-sm outline-none placeholder:text-background-50/30 disabled:opacity-40"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 hover:bg-primary-400 transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className={`${status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} text-background-50 text-sm`}></i>
                </button>
              </div>
              {status === 'success' && (
                <p className="mt-2 font-body text-xs text-green-400">Thank you for subscribing!</p>
              )}
              {status === 'error' && (
                <p className="mt-2 font-body text-xs text-red-400">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Lower Footer - Big Brand Text */}
      <div className="w-full overflow-hidden py-12 md:py-16 bg-foreground-900">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 text-center">
          <p className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-background-100/10 tracking-tight select-none">
            SUPERBUY
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-background-50/8">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-body text-xs text-background-50/35">
            &copy; 2026 Superbuy Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="font-body text-xs text-background-50/35 hover:text-background-50/60 transition-colors duration-200 cursor-pointer"
            >
              Main Store
            </a>
            <Link to="/" className="font-body text-xs text-background-50/35 hover:text-background-50/60 transition-colors duration-200">
              Home
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}